import React, { useState, useEffect, useRef } from "react";
import * as math from "mathjs";

// ──────────────────────────────────────────────────────────────────────
// JAUHARI AI — powered by Claude (claude-sonnet-4-20250514)
// Drop this component anywhere in your TeacherDashboard where the
// activeTab === "solver" block used to be.
// ──────────────────────────────────────────────────────────────────────

const SYSTEM_PROMPT = `You are Jauhari AI, an expert educational assistant for Indian school teachers and students. You answer questions across all subjects: Mathematics, Physics, Chemistry, Biology, History, Geography, Computer Science, English, Hindi, and General Knowledge.

Rules:
- Always respond in clear, structured, educational language.
- For maths expressions, show step-by-step working.
- For science topics, include formulas and examples.
- For Hindi/English, give meanings, synonyms, antonyms clearly.
- Use relevant emojis to make answers engaging (but don't overdo it).
- Keep answers concise but complete — suitable for school-level students.
- If the question is in Hindi, reply in Hindi + English.
- Format answers with bold section headers using **text** markdown style.
- Never refuse educational questions.
- Sign off with: ⚡ Powered by Jauhari AI`;

async function askClaude(userMessage) {
  // Try math first (fast, offline)
  const mathResult = tryMath(userMessage);
  if (mathResult) return mathResult;

  const response = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: 1000,
      system: SYSTEM_PROMPT,
      messages: [{ role: "user", content: userMessage }],
    }),
  });

  if (!response.ok) throw new Error(`API error ${response.status}`);
  const data = await response.json();
  return data.content?.[0]?.text || "Sorry, I could not generate a response.";
}

function tryMath(question) {
  try {
    const clean = question
      .replace(/×/g, "*")
      .replace(/÷/g, "/")
      .replace(/[^\d\+\-\*\/\(\)\^\%\.\,\s]/g, "")
      .trim();
    if (!clean || !/[\+\-\*\/\^]/.test(clean)) return null;
    const result = math.evaluate(clean);
    if (result === undefined || result === null) return null;
    const fmt = Number.isInteger(result) ? result : parseFloat(result.toFixed(6));
    return `🧮 **Math Result**\n\n**Expression:** ${question.trim()}\n**Answer:** ${fmt}\n\n📐 *Calculated using BODMAS/PEMDAS order of operations.*\n\n⚡ Powered by Jauhari AI`;
  } catch {
    return null;
  }
}

// ─── Formatting: render **bold** and newlines ───────────────────────
function FormattedAnswer({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  return (
    <div style={{ lineHeight: 1.85, fontSize: 14, color: "#1a2e1a" }}>
      {lines.map((line, i) => {
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
          <p key={i} style={{ margin: "3px 0" }}>
            {parts.map((part, j) =>
              part.startsWith("**") && part.endsWith("**") ? (
                <strong key={j}>{part.slice(2, -2)}</strong>
              ) : (
                <span key={j}>{part}</span>
              )
            )}
          </p>
        );
      })}
    </div>
  );
}

// ─── Quick-fill example chips ────────────────────────────────────────
const EXAMPLES = [
  { label: "🧮 Math", q: "2*5/8-9+20" },
  { label: "🔬 Physics", q: "What is Newton's first law of motion?" },
  { label: "⚗️ Chemistry", q: "What is the chemical formula for water?" },
  { label: "🌿 Biology", q: "Explain photosynthesis with equation" },
  { label: "📜 History", q: "Who was Emperor Ashoka?" },
  { label: "🌍 Geography", q: "Where is the Ganga river and how long is it?" },
  { label: "💻 Computer", q: "What is the full form of CPU and RAM?" },
  { label: "📖 English", q: "Synonyms and antonyms of beautiful" },
  { label: "🇮🇳 Hindi", q: "पानी का English meaning और synonyms बताओ" },
  { label: "🏛️ GK", q: "What is the national animal of India?" },
];

export default function JauhariAISolver() {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jauhari_history") || "[]"); }
    catch { return []; }
  });
  const [dots, setDots] = useState("");
  const answerRef = useRef(null);
  const timerRef = useRef(null);

  // Animated dots while loading
  useEffect(() => {
    if (loading) {
      timerRef.current = setInterval(() =>
        setDots(d => (d.length >= 3 ? "" : d + ".")), 400);
    } else {
      clearInterval(timerRef.current);
      setDots("");
    }
    return () => clearInterval(timerRef.current);
  }, [loading]);

  const ask = async () => {
    const q = question.trim();
    if (!q) return;
    setLoading(true);
    setError("");
    setAnswer("");

    try {
      const result = await askClaude(q);
      setAnswer(result);
      const newHistory = [{ q, a: result, ts: Date.now() }, ...history].slice(0, 12);
      setHistory(newHistory);
      localStorage.setItem("jauhari_history", JSON.stringify(newHistory));
      setTimeout(() => answerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    } catch (e) {
      setError("⚠️ Could not reach Jauhari AI. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) ask();
  };

  return (
    <div style={S.wrap}>
      {/* Header */}
      <div style={S.header}>
        <div style={S.brainWrap}>
          <span style={S.brainEmoji}>🧠</span>
          <div style={S.pulse} />
        </div>
        <div>
          <h2 style={S.title}>Jauhari AI</h2>
          <p style={S.subtitle}>Powered by Jauhari WebTech· All Subjects · Instant Answers</p>
        </div>
      </div>

      {/* Input */}
      <div style={S.inputWrap}>
        <textarea
          rows={5}
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={handleKey}
          placeholder={`Ask anything…\n• 2*5/8-9+20\n• What is Newton's first law?\n• Explain photosynthesis\n• पानी का English meaning\n\nPress Ctrl+Enter to send`}
          style={S.textarea}
          disabled={loading}
        />
        <button onClick={ask} style={{ ...S.btn, ...(loading ? S.btnLoading : {}) }} disabled={loading}>
          {loading ? `🧠 Thinking${dots}` : "🚀 Ask Jauhari AI"}
        </button>
      </div>

      {/* Error */}
      {error && <div style={S.errorBox}>{error}</div>}

      {/* Answer */}
      {answer && (
        <div style={S.answerBox} ref={answerRef}>
          <div style={S.answerHeader}>
            <span style={{ fontSize: 20 }}>🧠</span>
            <strong style={{ color: "#166534", fontSize: 13 }}>Jauhari AI Response</strong>
          </div>
          <FormattedAnswer text={answer} />
          <div style={S.answerFooter}>
            <span>⚡ Jauhari AI</span>
            <span>🎓 Powered by Claude</span>
            <span>📚 All Subjects</span>
          </div>
        </div>
      )}

      {/* Example chips */}
      <div style={S.section}>
        <p style={S.sectionLabel}>💡 Quick Examples</p>
        <div style={S.chips}>
          {EXAMPLES.map(ex => (
            <button key={ex.q} style={S.chip}
              onClick={() => { setQuestion(ex.q); setAnswer(""); setError(""); }}>
              {ex.label}
            </button>
          ))}
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div style={S.section}>
          <p style={S.sectionLabel}>📜 Recent Questions</p>
          {history.map((item, i) => (
            <div key={i} style={S.histItem}
              onClick={() => { setQuestion(item.q); setAnswer(item.a); setError(""); }}>
              <span style={{ fontSize: 14 }}>💬</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={S.histQ}>{item.q.length > 70 ? item.q.slice(0, 70) + "…" : item.q}</p>
                <p style={S.histTs}>{new Date(item.ts).toLocaleString("en-IN")}</p>
              </div>
              <span style={S.histArrow}>›</span>
            </div>
          ))}
          <button style={S.clearBtn} onClick={() => { setHistory([]); localStorage.removeItem("jauhari_history"); }}>
            Clear History
          </button>
        </div>
      )}

      {/* Subject badges */}
      <div style={S.badges}>
        {["🔢 Maths","🔬 Physics","⚗️ Chemistry","🌿 Biology","📜 History",
          "🌍 Geography","💻 Computer","📖 English","🇮🇳 Hindi","🏛️ GK"].map(b => (
          <span key={b} style={S.badge}>{b}</span>
        ))}
      </div>
    </div>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────
const S = {
  wrap: { background: "linear-gradient(135deg,#fff 0%,#f0f4ff 100%)", borderRadius: 20, padding: 24, boxShadow: "0 4px 20px rgba(0,0,0,0.10)", border: "1px solid #e2e8f0", marginBottom: 20, fontFamily: "'Segoe UI',system-ui,sans-serif" },
  header: { display: "flex", alignItems: "center", gap: 14, marginBottom: 22, borderBottom: "1px solid #e2e8f0", paddingBottom: 18 },
  brainWrap: { position: "relative", width: 52, height: 52 },
  brainEmoji: { fontSize: 42, lineHeight: 1 },
  pulse: { position: "absolute", inset: -4, borderRadius: "50%", border: "2px solid #667eea", opacity: 0.5, animation: "jauhariPulse 2s ease-in-out infinite" },
  title: { fontSize: 22, fontWeight: 700, color: "#667eea", margin: 0 },
  subtitle: { fontSize: 12, color: "#718096", margin: "3px 0 0" },
  inputWrap: { display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 },
  textarea: { width: "100%", padding: 14, borderRadius: 12, border: "2px solid #e2e8f0", fontSize: 14, fontFamily: "inherit", resize: "vertical", outline: "none", boxSizing: "border-box", lineHeight: 1.6, transition: "border-color 0.2s" },
  btn: { padding: "13px 0", background: "linear-gradient(135deg,#667eea,#764ba2)", color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: "pointer", letterSpacing: "0.3px", transition: "all 0.3s" },
  btnLoading: { background: "#a0aec0", cursor: "not-allowed" },
  errorBox: { background: "#fff5f5", border: "1px solid #feb2b2", borderRadius: 10, padding: "12px 16px", color: "#c53030", fontSize: 13, marginBottom: 14 },
  answerBox: { background: "#f0fff4", border: "2px solid #9ae6b4", borderRadius: 16, padding: 18, marginBottom: 20 },
  answerHeader: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12, paddingBottom: 10, borderBottom: "1px solid #c6f6d5" },
  answerFooter: { display: "flex", justifyContent: "space-between", marginTop: 14, paddingTop: 10, borderTop: "1px solid #c6f6d5", fontSize: 11, color: "#48bb78", flexWrap: "wrap", gap: 6 },
  section: { marginTop: 18 },
  sectionLabel: { fontSize: 13, fontWeight: 600, color: "#4a5568", margin: "0 0 10px" },
  chips: { display: "flex", flexWrap: "wrap", gap: 8 },
  chip: { padding: "7px 14px", background: "#edf2f7", border: "none", borderRadius: 25, fontSize: 12, cursor: "pointer", fontWeight: 600, color: "#4a5568", transition: "all 0.2s", fontFamily: "inherit" },
  histItem: { display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "#fff", borderRadius: 10, marginBottom: 8, cursor: "pointer", border: "1px solid #e2e8f0", transition: "background 0.2s" },
  histQ: { margin: 0, fontSize: 13, color: "#2d3748", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  histTs: { margin: "2px 0 0", fontSize: 10, color: "#a0aec0" },
  histArrow: { fontSize: 18, color: "#cbd5e0" },
  clearBtn: { background: "none", border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 14px", fontSize: 11, color: "#a0aec0", cursor: "pointer", marginTop: 6, fontFamily: "inherit" },
  badges: { display: "flex", flexWrap: "wrap", gap: 7, marginTop: 18, justifyContent: "center" },
  badge: { padding: "5px 11px", background: "#edf2f7", borderRadius: 20, fontSize: 11, fontWeight: 500, color: "#4a5568" },
};

// Inject keyframes once
if (!document.getElementById("jauhari-style")) {
  const el = document.createElement("style");
  el.id = "jauhari-style";
  el.textContent = `@keyframes jauhariPulse { 0%,100%{transform:scale(1);opacity:0.5} 50%{transform:scale(1.3);opacity:0.15} }`;
  document.head.appendChild(el);
}
